import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  authLogin,
  sendOtp,
  verifyAuthToken,
} from "../../services/authService";
import { setProfile } from "./profileSlice";
import { toast } from "react-toastify";
import { apiRequest } from "../../util/apiUtils";

const access_token = localStorage.getItem("auth_token");

const initialState = {
  session: { user: null, status: "loading", error: null },
  loginPending: false,
  loginError: null,
  loginData: null,
};

// Thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async (
    { redirect = false, type = "user", navigate, location, ...val },
    { dispatch }
  ) => {
    const res = await authLogin({ ...val, type });
    if (res?.status === 200) {
      toast.success(res?.message);
      if (res?.redirectionLink?.accept) {
        if (res.forcePassword && type === "user") {
          navigate("/reset-password", {
            state: { email: val?.email, forcePassword: res?.forcePassword },
          });
        } else {
          navigate("/accept-user");
        }
      } else {
        const { accessToken, refreshToken } = res;
        localStorage.setItem("auth_token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        const verify = await apiRequest(
          "post",
          `verify-token`,
          {
            body: {},
          },
          "application/json",
          () => {},
          {
            Authorization: "Bearer " + res?.accessToken,
          }
        );

        if (verify?.data?.status === 200) {
          const user = verify?.data?.data || {};

          dispatch(
            setProfile({ ...user, loading: false, access_token: accessToken })
          );
          dispatch(
            setSession({
              user: { ...user, access_token: accessToken },
              status: "authorized",
              error: null,
            })
          );

          const params = new URLSearchParams(location?.search);
          const callback = params.get("callback");

          if (callback) {
            navigate(callback);
          } else {
            window.location.href = window.location.origin + redirect;
          }
        }
      }
    } else if (res?.status === 400) {
      if (res?.data?.isNewUser) {
        sendOtp({
          email: val?.email,
          requestType: "SIGNUP",
          verificationType: "email-verification",
        }).then((resp) => {
          if (resp?.status === 200) {
            toast.info("Verify email first");
            navigate("/account-verification", {
              state: { email: val?.email },
            });
          }
        });
      } else {
        toast.error(res?.message || "Login failed. Please try again.");
      }
    }
    return res;
  }
);

// Thunk for verifying auth token
export const verifyAuth = createAsyncThunk(
  "auth/verifyAuth",
  async (_, { dispatch }) => {
    const res = await verifyAuthToken();
    const accessToken = localStorage.getItem("auth_token");

    if (res?.status === 200) {
      dispatch(
        setProfile({ ...res?.data, loading: false, access_token: accessToken })
      );
      localStorage.setItem("userId", res?.data?._id);
      return {
        user: { ...res?.data, access_token: accessToken },
        status: "authorized",
        error: null,
      };
    } else if (res?.data?.invalidToken) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refreshToken");
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: "Invalid token",
      };
    } else {
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: null,
      };
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession: (state, action) => {
      state.session = { ...state.session, ...action.payload };
    },
    setLoginData: (state, action) => {
      state.loginData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loginPending = true;
        state.loginError = null;
        state.loginData = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginPending = false;
        state.loginData = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.loginPending = false;
        state.loginError = action.error.message;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.session = {
          user: action.payload.user,
          status: action.payload.status,
          error: action.payload.error,
        };
      });
  },
});

export const { setSession, setLoginData } = authSlice.actions;
export default authSlice.reducer;
